/**
 * StyleX design tokens for the Primaris design system.
 *
 * - defineVars: Creates CSS custom properties (themeable via createTheme)
 * - defineConsts: Inlined at build-time (not themeable, better performance)
 *
 * NOTE: Values must be inlined - StyleX cannot resolve imports at compile time.
 * Keep in sync with @animus/palette and @animus/theme.
 */

import * as stylex from "@stylexjs/stylex";

/**
 * Semantic color tokens (themeable via CSS variables)
 *
 * These reference CSS variables set by PrimarisProvider.
 * Values come from @animus/palette at runtime.
 */
export const colors = stylex.defineVars({
  // Special
  transparent: "transparent",
  current: "currentColor",

  // Backgrounds
  background: "var(--primaris-background)",
  backgroundSubtle: "var(--primaris-background-subtle)",
  backgroundMuted: "var(--primaris-background-muted)",

  // Foregrounds (text)
  foreground: "var(--primaris-foreground)",
  foregroundMuted: "var(--primaris-foreground-muted)",
  foregroundSubtle: "var(--primaris-foreground-subtle)",

  // Cards & surfaces
  card: "var(--primaris-card)",
  cardForeground: "var(--primaris-card-foreground)",

  // Borders
  border: "var(--primaris-border)",
  borderSubtle: "var(--primaris-border-subtle)",

  // Inputs
  input: "var(--primaris-input)",
  inputForeground: "var(--primaris-input-foreground)",
  ring: "var(--primaris-ring)",

  // Primary (brand/accent)
  primary: "var(--primaris-primary)",
  primaryForeground: "var(--primaris-primary-foreground)",
  primaryHover: "var(--primaris-primary-hover)",

  // Secondary
  secondary: "var(--primaris-secondary)",
  secondaryForeground: "var(--primaris-secondary-foreground)",

  // Muted (subtle elements)
  muted: "var(--primaris-muted)",
  mutedForeground: "var(--primaris-muted-foreground)",

  // Accent (highlights)
  accent: "var(--primaris-accent)",
  accentForeground: "var(--primaris-accent-foreground)",

  // Semantic states
  destructive: "var(--primaris-destructive)",
  destructiveForeground: "var(--primaris-destructive-foreground)",

  success: "var(--primaris-success)",
  successForeground: "var(--primaris-success-foreground)",

  warning: "var(--primaris-warning)",
  warningForeground: "var(--primaris-warning-foreground)",

  info: "var(--primaris-info)",
  infoForeground: "var(--primaris-info-foreground)",

  // Component-specific
  avatarInset: "var(--primaris-avatar-inset)",
});

/**
 * Glassmorphism tokens (themeable via CSS variables)
 */
export const glass = stylex.defineVars({
  surface: "var(--primaris-glass-surface)",
  surfaceSubtle: "var(--primaris-glass-surface-subtle)",
  surfaceElevated: "var(--primaris-glass-surface-elevated)",
  border: "var(--primaris-glass-border)",
  borderSubtle: "var(--primaris-glass-border-subtle)",
});

/**
 * Spacing tokens in pixels (inlined)
 */
export const space = stylex.defineConsts({
  none: "0px",
  "2xs": "2px",
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "24px",
  "2xl": "32px",
  "3xl": "48px",
  "4xl": "64px",
  "5xl": "96px",
});

/**
 * Border radius tokens in pixels (inlined)
 * More generous for modern, Apple-like feel
 */
export const radius = stylex.defineConsts({
  none: "0px",
  sm: "4px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  "2xl": "20px",
  "3xl": "24px",
  full: "9999px",
});

/**
 * Font size tokens in pixels (inlined)
 * Refined for UI clarity
 */
export const fontSize = stylex.defineConsts({
  "2xs": "10px",
  xs: "11px",
  sm: "13px",
  md: "14px",
  lg: "15px",
  xl: "18px",
  "2xl": "22px",
  "3xl": "28px",
  "4xl": "36px",
  "5xl": "48px",
  "6xl": "64px",
});

/**
 * Line height tokens (inlined)
 */
export const lineHeight = stylex.defineConsts({
  none: "1",
  tight: "1.2",
  snug: "1.35",
  normal: "1.5",
  relaxed: "1.65",
  loose: "2",
});

/**
 * Letter spacing tokens (inlined)
 *
 * Inter looks best with slightly negative tracking.
 * Default (-0.011em) is optimized for Inter at body sizes.
 */
export const letterSpacing = stylex.defineConsts({
  tighter: "-0.02em",
  tight: "-0.011em",
  normal: "0",
  wide: "0.02em",
  wider: "0.04em",
});

/**
 * Font weight tokens (inlined)
 *
 * Inter variable font supports continuous weights 100-900.
 * Standard weights (400, 500, 600, 700) work with any font.
 * Custom weights (480, 530, 750) leverage Inter's variable axis for finer control.
 */
export const fontWeight = stylex.defineConsts({
  thin: "100",
  extralight: "200",
  light: "300",
  normal: "400",
  preMedium: "480",
  medium: "500",
  postMedium: "530",
  semibold: "600",
  bold: "700",
  extraBold: "750",
  black: "900",
});

/**
 * Z-index tokens (inlined)
 */
export const zIndex = stylex.defineConsts({
  behind: "-1",
  base: "0",
  raised: "10",
  dropdown: "100",
  sticky: "200",
  fixed: "300",
  modalBackdrop: "400",
  modal: "500",
  popover: "600",
  tooltip: "700",
  toast: "800",
  max: "9999",
});

/**
 * Animation duration tokens (inlined)
 */
export const duration = stylex.defineConsts({
  instant: "0ms",
  fastest: "50ms",
  faster: "100ms",
  fast: "150ms",
  normal: "200ms",
  slow: "300ms",
  slower: "400ms",
  slowest: "500ms",
});

/**
 * Animation easing tokens (inlined)
 */
export const easing = stylex.defineConsts({
  linear: "linear",
  default: "cubic-bezier(0.25, 0.1, 0.25, 1.0)",
  enter: "cubic-bezier(0.0, 0.0, 0.2, 1.0)",
  exit: "cubic-bezier(0.4, 0.0, 1.0, 1.0)",
  easeIn: "cubic-bezier(0.4, 0, 1, 1)",
  easeOut: "cubic-bezier(0, 0, 0.2, 1)",
  easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
  spring: "cubic-bezier(0.34, 1.56, 0.64, 1.0)",
  emphasized: "cubic-bezier(0.2, 0.0, 0.0, 1.0)",
});

/**
 * Shadow tokens (themeable)
 *
 * Uses warm-tinted shadows (espresso base) instead of pure black.
 * Glow shadows use brand pink for consistent visual identity.
 */
export const shadow = stylex.defineVars({
  none: "none",
  xs: "0 1px 2px rgba(42, 36, 34, 0.04)",
  sm: "0 2px 4px rgba(42, 36, 34, 0.06)",
  /** Soft shadow for floating elements: toasts, menus, popovers */
  soft: "0 2px 8px rgba(42, 36, 34, 0.08)",
  md: "0 4px 8px rgba(42, 36, 34, 0.08), 0 1px 2px rgba(42, 36, 34, 0.04)",
  lg: "0 8px 24px rgba(42, 36, 34, 0.12), 0 2px 8px rgba(42, 36, 34, 0.06)",
  xl: "0 16px 48px rgba(42, 36, 34, 0.16), 0 4px 16px rgba(42, 36, 34, 0.08)",
  "2xl": "0 24px 64px rgba(42, 36, 34, 0.20)",
  inner: "inset 0 1px 2px rgba(42, 36, 34, 0.08)",
  glow: "0 0 24px rgba(232, 136, 153, 0.24)",
  glowStrong: "0 0 32px rgba(232, 136, 153, 0.32)",
});

/**
 * Shadow values as plain strings for inline styles.
 * Use these when you can't use StyleX (e.g., third-party component props).
 */
export const shadowValue = {
  soft: "0 2px 8px rgba(42, 36, 34, 0.08)",
} as const;

/**
 * Glassmorphism effect values (inlined)
 */
export const glassEffect = stylex.defineConsts({
  blur: "20px",
  blurStrong: "32px",
  saturate: "180%",
});
